/**
 * Sales point context owner — Phase 2 §2.4 IMP-06 (facade over lastKnownSalesPoint).
 */
import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import {
  readLastKnownSalesPoint,
  rememberSalesPointForTenant,
  rememberSalesPointIdForTenant,
} from '../session/lastKnownSalesPoint.js';
import { useTenantCode } from '../tenant/TenantProvider.js';

export interface SalesPointContextValue {
  readonly salesPointId: number | null;
  readonly salesPointSlug: string | null;
  readonly rememberSalesPoint: (salesPointId: number, salesPointSlug: string) => void;
  readonly rememberSalesPointId: (salesPointId: number) => void;
  readonly refreshFromStorage: () => void;
}

const SalesPointContext = createContext<SalesPointContextValue | null>(null);

interface SalesPointProviderProps {
  readonly children: ReactNode;
}

export function SalesPointProvider({ children }: SalesPointProviderProps): JSX.Element {
  const tenantCode = useTenantCode();
  const initialSnapshot = readLastKnownSalesPoint(tenantCode);
  const [salesPointId, setSalesPointId] = useState<number | null>(
    () => initialSnapshot?.salesPointId ?? null,
  );
  const [salesPointSlug, setSalesPointSlug] = useState<string | null>(
    () => initialSnapshot?.salesPointSlug ?? null,
  );

  const refreshFromStorage = useCallback((): void => {
    const snapshot = readLastKnownSalesPoint(tenantCode);
    setSalesPointId(snapshot?.salesPointId ?? null);
    setSalesPointSlug(snapshot?.salesPointSlug ?? null);
  }, [tenantCode]);

  const rememberSalesPoint = useCallback(
    (nextSalesPointId: number, nextSalesPointSlug: string): void => {
      rememberSalesPointForTenant(tenantCode, nextSalesPointId, nextSalesPointSlug);
      setSalesPointId(nextSalesPointId);
      setSalesPointSlug(nextSalesPointSlug);
    },
    [tenantCode],
  );

  const rememberSalesPointId = useCallback(
    (nextSalesPointId: number): void => {
      rememberSalesPointIdForTenant(tenantCode, nextSalesPointId);
      setSalesPointId(nextSalesPointId);
    },
    [tenantCode],
  );

  const value = useMemo<SalesPointContextValue>(
    () => ({
      salesPointId,
      salesPointSlug,
      rememberSalesPoint,
      rememberSalesPointId,
      refreshFromStorage,
    }),
    [salesPointId, salesPointSlug, rememberSalesPoint, rememberSalesPointId, refreshFromStorage],
  );

  return (
    <SalesPointContext.Provider value={value}>{children}</SalesPointContext.Provider>
  );
}

export function useSalesPointContext(): SalesPointContextValue {
  const ctx = useContext(SalesPointContext);
  if (ctx === null) {
    throw new Error('useSalesPointContext must be used inside <SalesPointProvider>');
  }
  return ctx;
}
